import { useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal } from "lucide-react";
import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { ReasonDialog } from "../../components/dash/ReasonDialog";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const GROUP_ACTIONS = {
  DEACTIVATE: ["Nonaktifkan Organisasi", "Semua anggota kehilangan akses operasional, kebun berjalan ditandai untuk pemeriksaan PT SJI, dan lahan tidak lagi ditawarkan ke investor. Data tidak dihapus.", true],
  FREEZE: ["Bekukan Darurat", "Pembekuan darurat: akses semua anggota langsung dicabut, kebun berjalan ditandai untuk pemeriksaan, organisasi tidak tampil sebagai mitra aktif. Data tidak dihapus.", true],
  ACTIVATE: ["Aktifkan Kembali", "Anggota dapat masuk dan bekerja kembali. Tanda pemeriksaan kebun tetap sampai PT SJI menyelesaikannya.", false],
  ARCHIVE: ["Arsipkan Organisasi", "Organisasi dipindah ke arsip dan seluruh akses anggota dihentikan. Data, dokumen, dan riwayat investasi tetap tersimpan.", true],
};
const USER_ACTIONS = {
  DEACTIVATE: ["Nonaktifkan Pengguna", "Hanya akun ini yang diblokir dan sesi aktifnya dicabut. Organisasi tidak terpengaruh.", true],
  ACTIVATE: ["Aktifkan Kembali", "Pengguna dapat masuk kembali.", false],
  ARCHIVE: ["Arsipkan Pengguna", "Akun diarsipkan dan tidak dapat masuk. Data tetap tersimpan.", true],
};

const ActionButtons = ({ actions, defs, url, reload, prefix, id }) => {
  const [open, setOpen] = useState(null);
  const run = async (reason) => {
    try { await api.post(url, { action: open, reason }); toast.success(`${defs[open][0]} berhasil`); reload(); }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  return <>
    {actions.map((a) => (
      <Button key={a} size="sm" variant="outline" onClick={() => setOpen(a)} data-testid={`${prefix}control-${a.toLowerCase()}-${id}`}
        className={defs[a][2] ? "text-red-700 border-red-200" : "text-emerald-800 border-emerald-200"}>{defs[a][0]}</Button>
    ))}
    {open && <ReasonDialog open onOpenChange={(o) => !o && setOpen(null)} title={`${defs[open][0]}?`} description={defs[open][1]}
      confirmLabel={`Ya, ${defs[open][0]}`} danger={defs[open][2]} requireReason={open !== "ACTIVATE"} onConfirm={run} />}
  </>;
};

export const OrgControl = ({ row, reload, prefix }) => {
  const s = row.status || "ACTIVE";
  const actions = s === "ACTIVE" ? ["DEACTIVATE", "FREEZE", "ARCHIVE"] : s === "ARCHIVED" ? ["ACTIVATE"] : ["ACTIVATE", "ARCHIVE"];
  return <ActionButtons actions={actions} defs={GROUP_ACTIONS} url={`/admin/farmer-groups/${row.id}/control`} reload={reload} prefix={prefix} id={row.id} />;
};

const UserMoreMenu = ({ row, reload, prefix, canArchive, canDelete }) => {
  const [check, setCheck] = useState(null);
  const [archive, setArchive] = useState(false);
  const openDelete = async () => {
    try { setCheck((await api.get(`/admin/users/${row.id}/delete-check`)).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const run = async (reason) => {
    try { await api.delete(`/admin/users/${row.id}`, { data: { reason } }); toast.success("Pengguna dihapus permanen"); reload(); }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const runArchive = async (reason) => {
    try { await api.post(`/admin/users/${row.id}/control`, { action: "ARCHIVE", reason }); toast.success(`${USER_ACTIONS.ARCHIVE[0]} berhasil`); reload(); }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  if (!canArchive && !canDelete) return null;
  return <>
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`${prefix}control-more-${row.id}`}><MoreHorizontal className="h-3.5 w-3.5 mr-1.5" />Lainnya</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canArchive && <DropdownMenuItem className="text-red-700" onSelect={() => setArchive(true)} data-testid={`${prefix}control-archive-${row.id}`}>{USER_ACTIONS.ARCHIVE[0]}</DropdownMenuItem>}
        {canDelete && <DropdownMenuItem className="text-red-700" onSelect={openDelete} data-testid={`${prefix}control-delete-${row.id}`}>Hapus permanen</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
    {archive && <ReasonDialog open onOpenChange={(o) => !o && setArchive(false)} title={`${USER_ACTIONS.ARCHIVE[0]}?`} description={USER_ACTIONS.ARCHIVE[1]}
      confirmLabel={`Ya, ${USER_ACTIONS.ARCHIVE[0]}`} danger onConfirm={runArchive} />}
    {check && (check.canDelete
      ? <ReasonDialog open onOpenChange={(o) => !o && setCheck(null)} title="Hapus pengguna permanen?" description={`Akun ${row.name} akan dihapus permanen. Tindakan ini tidak dapat dibatalkan dan tercatat di Audit Log.`} confirmLabel="Ya, hapus permanen" danger onConfirm={run} />
      : <ReasonDialog open onOpenChange={(o) => !o && setCheck(null)} title="Tidak dapat dihapus permanen" description="Akun ini masih terkait data penting yang wajib disimpan. Gunakan Nonaktifkan atau Arsipkan." confirmLabel="Tutup" requireReason={false} hideInput hideCancel onConfirm={() => true}>
          <ul className="text-sm text-red-700 list-disc pl-5" data-testid="delete-blockers">{check.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        </ReasonDialog>)}
  </>;
};

export const UserControl = ({ row, reload, prefix }) => {
  const { user } = useAuth();
  if (row.id === user.id) return null;
  const s = row.status;
  const actions = s === "ACTIVE" ? ["DEACTIVATE", "ARCHIVE"] : s === "ARCHIVED" ? ["ACTIVATE"] : s === "PENDING" || s === "REJECTED" ? ["ARCHIVE"] : ["ACTIVATE", "ARCHIVE"];
  return <>
    <ActionButtons actions={actions.filter((a) => a !== "ARCHIVE")} defs={USER_ACTIONS} url={`/admin/users/${row.id}/control`} reload={reload} prefix={prefix} id={row.id} />
    <UserMoreMenu row={row} reload={reload} prefix={prefix} canArchive={actions.includes("ARCHIVE")} canDelete={row.role !== "SUPER_ADMIN"} />
  </>;
};

export const ReviewFlag = ({ row, reload, prefix }) => {
  const [open, setOpen] = useState(false);
  if (!row.reviewFlag) return null;
  const run = async (reason) => {
    try { await api.post(`/admin/projects/${row.id}/clear-review`, { reason }); toast.success("Pemeriksaan kebun selesai"); reload(); }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  return <>
    <Button size="sm" variant="outline" className="text-amber-800 border-amber-300" onClick={() => setOpen(true)} data-testid={`${prefix}projects-clear-review-${row.id}`}>Selesai Diperiksa</Button>
    {open && <ReasonDialog open onOpenChange={setOpen} title="Tandai pemeriksaan selesai?" description={row.reviewFlag.reason} confirmLabel="Selesai diperiksa" requireReason={false} onConfirm={run} />}
  </>;
};
